/**
 * Ops map region filter — pure helpers (no DOM, no fetch).
 *
 * Operator picks zero, one, or many Live zones (GET /zones). The map then
 * shows only those zones: fences, device pins, live thumbs. Empty = whole map.
 * Soft≠Live: filter never invents zones; it only hides Live geometry.
 * Lab persists in localStorage; production in sessionStorage.
 */
import { isLocalHost } from "./pathseal.js";

export const REGION_KEY = "croc_ops_region_filter_v1";
export const SITE_KEY = "croc_ops_site_filter_v1";
export const AUTO_LIVE_KEY = "croc_ops_auto_live_on_signal_v1";

function storeOf() {
  return isLocalHost() ? localStorage : sessionStorage;
}

/** @returns {string[]} zone ids (deduped, trimmed). */
export function loadRegionIds() {
  try {
    const raw = storeOf().getItem(REGION_KEY);
    const j = raw ? JSON.parse(raw) : [];
    return normalizeRegionIds(j);
  } catch {
    return [];
  }
}

export function saveRegionIds(ids) {
  try {
    const list = normalizeRegionIds(ids);
    if (list.length) storeOf().setItem(REGION_KEY, JSON.stringify(list));
    else storeOf().removeItem(REGION_KEY);
  } catch {
    /* */
  }
}

export function normalizeRegionIds(ids) {
  const out = [];
  const seen = new Set();
  for (const raw of Array.isArray(ids) ? ids : []) {
    const id = String(raw || "").trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/** Toggle one id in a multi-select list. */
export function toggleRegionId(ids, id) {
  const key = String(id || "").trim();
  const list = normalizeRegionIds(ids);
  if (!key) return list;
  return list.includes(key) ? list.filter((x) => x !== key) : [...list, key];
}

/**
 * Chips the operator can pick. Surveyed zones only.
 * A typed device.zone caption is not a region — geometry is the membership SoT.
 * @param {Array<{id?:string,label?:string}>} zones
 */
export function regionOptionsOf(zones) {
  const out = [];
  const seen = new Set();
  for (const z of Array.isArray(zones) ? zones : []) {
    if (!z) continue;
    const id = String(z.id || z.label || "").trim();
    const label = String(z.label || id).trim() || id;
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push({ id, label });
  }
  return out;
}

/**
 * Build a matcher from zone ids against surveyed zones.
 * Empty selection = whole map. A saved id that matches no surveyed zone
 * also means whole map, so a stale caption cannot blank the map.
 * Device pins follow geometric membership (deviceIds), not device.zone text.
 * @param {string[]} ids
 * @param {Array<{id:string,label:string,deviceIds?:string[]}>} zones
 */
export function regionMatcherOf(ids, zones) {
  const want = new Set(normalizeRegionIds(ids));
  const all = want.size === 0;
  const names = new Set();
  const devices = new Set();
  for (const z of Array.isArray(zones) ? zones : []) {
    if (!z) continue;
    const zid = String(z.id || "").trim();
    const label = String(z.label || "").trim();
    if (!(want.has(zid) || want.has(label))) continue;
    if (zid) names.add(zid);
    if (label) names.add(label);
    for (const d of Array.isArray(z.deviceIds) ? z.deviceIds : []) {
      const did = String(d || "").trim();
      if (did) devices.add(did);
    }
  }
  const open = all || names.size === 0;
  return {
    all: open,
    count: want.size,
    /** Fence / zone label or id. */
    zone: (nameOrId) => open || names.has(String(nameOrId || "").trim()),
    /** Device row: geometric membership only. */
    device: (d) => {
      if (open) return true;
      if (!d) return false;
      const id = String(d.id || "").trim();
      return !!(id && devices.has(id));
    },
    /** Live thumb row ({ zoneId, deviceId }). */
    thumb: (t) => {
      if (open) return true;
      if (!t) return false;
      if (names.has(String(t.zoneId || "").trim())) return true;
      return devices.has(String(t.deviceId || "").trim());
    },
  };
}

export function loadSiteIds() {
  try {
    const raw = storeOf().getItem(SITE_KEY);
    const j = raw ? JSON.parse(raw) : [];
    return normalizeRegionIds(j);
  } catch {
    return [];
  }
}

export function saveSiteIds(ids) {
  try {
    const list = normalizeRegionIds(ids);
    if (list.length) storeOf().setItem(SITE_KEY, JSON.stringify(list));
    else storeOf().removeItem(SITE_KEY);
  } catch {
    /* */
  }
}

/** Home site only. A polygon does not invent a home, and an empty home is not default. */
export function sitesOfDevice(device, zones) {
  void zones;
  const explicit = String((device && (device.site_id || device.site)) || "").trim();
  return explicit ? [explicit] : [];
}

/** A board is on this site only when its home site is this site. */
export function deviceHomedOnSite(device, siteId) {
  const home = String((device && (device.site_id || device.site)) || "").trim();
  const site = String(siteId || "").trim();
  return Boolean(home && site && home === site);
}

/** Empty site selection = every site. A selected site hides boards with no home. */
export function deviceMatchesSites(device, siteIds, zones) {
  const want = normalizeRegionIds(siteIds);
  if (!want.length) return true;
  return sitesOfDevice(device, zones).some((s) => want.includes(s));
}

export function fenceMatchesSites(name, siteIds, zones) {
  const want = normalizeRegionIds(siteIds);
  if (!want.length) return true;
  const key = String(name || "").trim();
  for (const z of Array.isArray(zones) ? zones : []) {
    if (!z) continue;
    const label = String(z.label || "").trim();
    const id = String(z.id || "").trim();
    if (key !== label && key !== id) continue;
    const sid = String((z.siteId || z.site_id) || "").trim();
    return Boolean(sid) && want.includes(sid);
  }
  return false;
}

export function siteLabelOf(ids, sites) {
  const list = normalizeRegionIds(ids);
  if (!list.length) return "";
  const byId = new Map();
  for (const s of Array.isArray(sites) ? sites : []) {
    if (!s) continue;
    const id = String(s.site_id || s.id || "").trim();
    if (id) byId.set(id, String(s.label || s.name || id));
  }
  return list.map((id) => byId.get(id) || id).join(" · ");
}

/** Human chip text: "Sibu · Bintulu" (labels resolved from zones). */
export function regionLabelOf(ids, zones) {
  const list = normalizeRegionIds(ids);
  if (!list.length) return "";
  const byId = new Map();
  for (const z of Array.isArray(zones) ? zones : []) {
    if (!z) continue;
    if (z.id) byId.set(String(z.id), String(z.label || z.id));
    if (z.label) byId.set(String(z.label), String(z.label));
  }
  return list.map((id) => byId.get(id) || id).join(" · ");
}

/**
 * Auto-open live on signal.
 * "1" explicit on, "0" explicit off, "" unset.
 * Unset follows AI custody: AUTO opens the window, anything else stays off.
 */
export function loadAutoLivePref() {
  try {
    const v = storeOf().getItem(AUTO_LIVE_KEY);
    return v === "1" || v === "0" ? v : "";
  } catch {
    return "";
  }
}

export function saveAutoLivePref(pref) {
  try {
    if (pref === "1" || pref === "0") storeOf().setItem(AUTO_LIVE_KEY, pref);
    else storeOf().removeItem(AUTO_LIVE_KEY);
  } catch {
    /* */
  }
}

export function autoLiveEnabled(pref, custodyMode) {
  if (pref === "1") return true;
  if (pref === "0") return false;
  return String(custodyMode || "").trim() === "AUTO";
}

/**
 * Pick the newest open alarm that should auto-open a live window.
 * Pure: returns null when nothing new, when disabled, or when the device is
 * outside the active region filter.
 * @param {object} args
 * @param {boolean} args.enabled
 * @param {Array<{id:string,dev:string,open:boolean,at?:string}>} args.alarms
 * @param {Set<string>} args.seen alarm ids already handled
 * @param {(d:object)=>boolean} args.deviceOk region matcher
 * @param {Array<{id:string}>} args.devices
 */
export function nextAutoLiveAlarm({ enabled, alarms, seen, deviceOk, devices }) {
  if (!enabled) return null;
  const rows = (Array.isArray(alarms) ? alarms : []).filter((a) => a && a.open && a.dev);
  const byId = new Map();
  for (const d of Array.isArray(devices) ? devices : []) {
    if (d && d.id) byId.set(String(d.id), d);
  }
  for (const a of rows) {
    const aid = String(a.id || "");
    if (!aid || (seen && seen.has(aid))) continue;
    const dev = byId.get(String(a.dev));
    if (!dev) continue;
    if (typeof deviceOk === "function" && !deviceOk(dev)) continue;
    return { alarm: a, device: dev };
  }
  return null;
}
